import React, { useState } from 'react';
import { 
  CheckCircle, Code, Smartphone, Palette, Database, Cloud, Shield,
  Zap, Clock, MessageSquare
} from 'lucide-react';

export default function Services({ darkMode, language }) {
  const [activeTab, setActiveTab] = useState('all');
  const isRTL = language === 'ar';
  
  const content = {
    fr: {
      title: "Services Professionnels",
      subtitle: "Solutions digitales complètes avec approche personnalisée",
      
      highlights: [
        {
          icon: Zap,
          title: "Rapidité d'exécution",
          description: "Délais optimisés sans compromis sur la qualité"
        },
        {
          icon: Clock,
          title: "Disponibilité",
          description: "Suivi continu et réactif"
        },
        {
          icon: Shield,
          title: "Garantie",
          description: "Support post-livraison inclus"
        }
      ],
      
      filterAll: 'Tous',
      filterDev: 'Développement',
      filterDesign: 'Design',
      filterCloud: 'Infrastructure',
      
      services: [
        {
          id: 'web',
          category: 'dev',
          icon: Code,
          title: "Développement Web Rapide",
          description: "Sites vitrine et applications web performants avec les dernières technologies.",
          features: ["Site vitrine optimisé SEO", "Application web sur mesure", "E-commerce & boutique en ligne", "Portfolio professionnel", "Landing page performante", "Optimisation mobile", "Hébergement inclus"],
          highlight: true,
          popular: true
        },
        {
          id: 'mobile',
          category: 'dev',
          icon: Smartphone,
          title: "Applications Mobiles",
          description: "Apps natives et hybrides pour iOS et Android avec interface moderne et intuitive.",
          features: ["Application iOS & Android", "React Native optimisé", "UI/UX personnalisée", "Notifications push", "Intégration backend", "Publication sur stores", "Maintenance incluse"],
          highlight: false,
          popular: false
        },
        {
          id: 'api',
          category: 'dev',
          icon: Database,
          title: "Systèmes & APIs",
          description: "Backend robuste et APIs sécurisées pour vos applications métier.",
          features: ["API RESTful/GraphQL", "Base de données MySQL", "Authentification avancée", "Websockets temps réel", "Microservices", "Documentation complète", "Monitoring"],
          highlight: true,
          popular: false
        },
        {
          id: 'design',
          category: 'design',
          icon: Palette,
          title: "UI/UX Design Expert",
          description: "Design centré utilisateur pour une expérience fluide et engageante.",
          features: ["Wireframes & maquettes", "Prototype interactif", "Design system complet", "Identité visuelle", "Responsive design", "Tests utilisateurs", "Assets exportés"],
          highlight: false,
          popular: true
        },
        {
          id: 'cloud',
          category: 'cloud',
          icon: Cloud,
          title: "Hébergement & DevOps",
          description: "Déploiement cloud sécurisé avec monitoring et sauvegarde automatique.",
          features: ["Hébergement AWS/Azure", "Configuration SSL", "Backup automatique", "Monitoring 24/7", "CDN optimisé", "Sécurité avancée", "CI/CD Pipeline"],
          highlight: false,
          popular: false
        },
        {
          id: 'consulting',
          category: 'dev',
          icon: MessageSquare,
          title: "Consultation & Négociation",
          description: "Accompagnement personnalisé pour définir votre projet et trouver la meilleure solution.",
          features: ["Analyse des besoins", "Étude de faisabilité", "Proposition technique", "Négociation personnalisée", "Planification détaillée", "Conseils stratégiques", "Support continu"],
          highlight: true,
          popular: true
        }
      ],
      
      processTitle: "Comment ça marche ?",
      processSteps: [
        {
          step: 1,
          title: "Consultation",
          description: "Nous discutons de votre projet, analysons vos besoins et établissons un cahier des charges."
        },
        {
          step: 2,
          title: "Proposition",
          description: "Proposition de solutions adaptées à votre situation spécifique."
        },
        {
          step: 3,
          title: "Développement",
          description: "Implémentation technique avec des livraisons régulières pour validation."
        },
        {
          step: 4,
          title: "Livraison",
          description: "Mise en production, formation et support post-livraison."
        }
      ]
    },
    ar: {
      title: "الخدمات المهنية",
      subtitle: "حلول رقمية متكاملة مع نهج شخصي",
      
      highlights: [
        {
          icon: Zap,
          title: "سرعة التنفيذ",
          description: "مواعيد نهائية محسنة دون المساس بالجودة"
        },
        {
          icon: Clock,
          title: "التوفر",
          description: "متابعة مستمرة وسريعة"
        },
        {
          icon: Shield,
          title: "الضمان",
          description: "دعم بعد التسليم مشمول"
        }
      ],
      
      filterAll: 'الكل',
      filterDev: 'التطوير',
      filterDesign: 'التصميم',
      filterCloud: 'البنية التحتية',
      
      services: [
        {
          id: 'web',
          category: 'dev',
          icon: Code,
          title: "تطوير ويب سريع",
          description: "مواقع تعريفية وتطبيقات ويب عالية الأداء بأحدث التقنيات.",
          features: ["موقع محسن لمحركات البحث", "تطبيق ويب مخصص", "متجر إلكتروني", "محمول مهني", "صفحة هبوط فعالة", "تحسين للموبايل", "استضافة مشمولة"],
          highlight: true,
          popular: true
        },
        {
          id: 'mobile',
          category: 'dev',
          icon: Smartphone,
          title: "تطبيقات الموبايل",
          description: "تطبيقات أصلية وهجينة لنظامي iOS و Android بواجهة حديثة وبديهية.",
          features: ["تطبيق iOS & Android", "React Native محسن", "واجهة مستخدم مخصصة", "إشعارات فورية", "تكامل خادم خلفي", "النشر على المتاجر", "صيانة مشمولة"],
          highlight: false,
          popular: false
        },
        {
          id: 'api',
          category: 'dev',
          icon: Database,
          title: "أنظمة و APIs",
          description: "خادم خلفي قوي و APIs آمنة لتطبيقات عملك.",
          features: ["API RESTful/GraphQL", "قاعدة بيانات MySQL", "مصادقة متقدمة", "Websockets وقت حقيقي", "خدمات صغيرة", "توثيق كامل", "المراقبة"],
          highlight: true,
          popular: false
        },
        {
          id: 'design',
          category: 'design',
          icon: Palette,
          title: "تصميم UI/UX متخصص",
          description: "تصميم يركز على المستخدم لتجربة سلسة وجذابة.",
          features: ["إطارات سلكية وتصاميم", "نموذج تفاعلي", "نظام تصميم كامل", "هوية بصرية", "تصميم متجاوب", "اختبارات المستخدمين", "أصول مصدرة"],
          highlight: false,
          popular: true
        },
        {
          id: 'cloud',
          category: 'cloud',
          icon: Cloud,
          title: "الاستضافة و DevOps",
          description: "نشر سحابي آمن مع مراقبة ونسخ احتياطي تلقائي.",
          features: ["استضافة AWS/Azure", "تهيئة SSL", "نسخ احتياطي تلقائي", "مراقبة 24/7", "CDN محسن", "أمان متقدم", "خط أنابيب CI/CD"],
          highlight: false,
          popular: false
        },
        {
          id: 'consulting',
          category: 'dev',
          icon: MessageSquare,
          title: "استشارة وتفاوض",
          description: "مرافقة مخصصة لتحديد مشروعك وإيجاد أفضل حل.",
          features: ["تحليل الاحتياجات", "دراسة الجدوى", "اقتراح تقني", "تفاوض مخصص", "تخطيط مفصل", "نصائح إستراتيجية", "دعم مستمر"],
          highlight: true,
          popular: true
        }
      ],
      
      processTitle: "كيف تعمل؟",
      processSteps: [
        {
          step: 1,
          title: "الاستشارة",
          description: "نناقش مشروعك، نحلل احتياجاتك ونضع دفتر الشروط."
        },
        {
          step: 2,
          title: "الاقتراح",
          description: "عرض حلول مكيفة لحالتك الخاصة."
        },
        {
          step: 3,
          title: "التطوير",
          description: "تنفيذ تقني مع تسليمات منتظمة للتحقق."
        },
        {
          step: 4,
          title: "التسليم",
          description: "الإنتاج، التدريب والدعم بعد التسليم."
        }
      ]
    }
  };

  const t = content[language];
  const filteredServices = activeTab === 'all' 
    ? t.services 
    : t.services.filter(service => service.category === activeTab);

  return (
    <section className="px-4 pt-8 min-h-screen flex items-start">
      <div className="max-w-7xl mx-auto w-full flex flex-col sm:flex-row gap-6 sm:gap-12 items-start">
        
        {/* Sidebar avec filtres et highlights (gauche) */}
        <div className="w-full sm:w-1/3">
          <div className="sticky top-8 space-y-8">
            {/* Titre principal */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-2xl shadow-lg`}>
              <h1 className={`text-3xl font-bold mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                {t.title}
              </h1>
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} ${isRTL ? 'font-arabic' : ''}`}>
                {t.subtitle}
              </p>
            </div>

            {/* Filtres */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className={`font-bold text-lg mb-4 ${isRTL ? 'font-arabic text-right' : ''}`}>
                {isRTL ? 'تصفية الخدمات' : 'Filtrer les services'}
              </h3>
              <div className="flex flex-col gap-2">
                {['all', 'dev', 'design', 'cloud'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 rounded-lg font-medium transition-colors text-left ${isRTL ? 'text-right' : ''} ${
                      activeTab === tab
                        ? 'bg-blue-600 text-white'
                        : darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {t[`filter${tab.charAt(0).toUpperCase() + tab.slice(1)}`]}
                  </button>
                ))}
              </div>
            </div>

            {/* Highlights */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className={`font-bold text-lg mb-4 ${isRTL ? 'font-arabic text-right' : ''}`}>
                {isRTL ? 'مزايا العمل معنا' : 'Avantages'}
              </h3>
              <div className="space-y-4">
                {t.highlights.map((highlight, index) => {
                  const Icon = highlight.icon;
                  return (
                    <div key={index} className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <Icon className="text-blue-500" size={20} />
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <h4 className={`font-bold text-sm ${isRTL ? 'font-arabic' : ''}`}>{highlight.title}</h4>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${isRTL ? 'font-arabic' : ''}`}>
                          {highlight.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Processus */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className={`font-bold text-lg mb-6 ${isRTL ? 'font-arabic text-right' : ''}`}>
                {t.processTitle}
              </h3>
              <div className="space-y-4">
                {t.processSteps.map((step, index) => (
                  <div key={index} className="flex gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                      darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {step.step}
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <h4 className={`font-bold text-sm ${isRTL ? 'font-arabic' : ''}`}>{step.title}</h4>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${isRTL ? 'font-arabic' : ''}`}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Grille des services (droite) */}
        <div className="w-full sm:w-2/3">
          <div className="grid md:grid-cols-2 gap-6">
            {filteredServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className={`rounded-2xl overflow-hidden border transition-all duration-300 hover:shadow-xl ${
                    service.highlight
                      ? 'border-blue-500 shadow-lg'
                      : darkMode
                      ? 'border-gray-700'
                      : 'border-gray-200'
                  } ${darkMode ? 'bg-gray-800' : 'bg-white'} relative`}
                >
                  {service.popular && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                        {isRTL ? 'شائع' : 'Populaire'}
                      </span>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className={`p-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 w-fit mb-4`}>
                      <Icon size={24} color="white" />
                    </div>
                    
                    <h3 className={`text-xl font-bold mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                      {service.title}
                    </h3>
                    
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 text-sm ${isRTL ? 'font-arabic' : ''}`}>
                      {service.description}
                    </p>

                    {/* Badge RECOMMANDÉ seulement (sans prix) */}
                    {service.highlight && (
                      <div className="mb-4">
                        <div className="text-xs font-semibold px-3 py-1 bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-full inline-block">
                          {isRTL ? 'مُوصى به' : 'RECOMMANDÉ'}
                        </div>
                      </div>
                    )}

                    {/* Features */}
                    <ul className="space-y-2">
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                          <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${isRTL ? 'font-arabic text-right' : ''}`}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}